(() => {
  if (customElements.get('yahav-sticky-atc')) return;

  const money = (cents, format) => {
    const fmt = format || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const [whole, decimals] = value.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_no_decimals') return withCommas;
      if (key === 'amount_with_comma_separator') return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimals}`;
      if (key === 'amount_no_decimals_with_comma_separator') return whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return `${withCommas}.${decimals}`;
    }).replace(/<[^>]*>/g, '');
  };

  // Slides up once the product's buy row scrolls above the header and hides at the footer.
  // Its button presses the real Add to Cart, so quantity, variant and the cart drawer stay in one place.
  class YahavStickyAtc extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[data-yp]');
      this.buyRow = this.root?.querySelector('.yp__buy');
      this.atc = this.root?.querySelector('[data-yp-atc]');
      this.button = this.querySelector('[data-sticky-atc]');
      if (!this.buyRow || !this.atc || !this.button || !('IntersectionObserver' in window)) return;

      this.pastBuy = false;
      this.atFooter = false;
      this.price = this.querySelector('[data-sticky-price]');
      this.compare = this.querySelector('[data-sticky-compare]');
      this.variantLabel = this.querySelector('[data-sticky-variant]');
      this.image = this.querySelector('[data-sticky-image]');
      this.labels = { add: this.root.dataset.labelAdd, soldOut: this.root.dataset.labelSoldOut, unavailable: this.root.dataset.labelUnavailable };
      try {
        this.productId = JSON.parse(this.root.querySelector('[data-yp-product]').textContent).id;
      } catch (_) {
        this.productId = null;
      }

      // With a design request on the page, the button leads back to it so the idea comes before the cart.
      this.design = this.root.querySelector('[data-yp-design]');
      this.designLabel = this.design?.dataset.stickyLabel || '';
      if (this.designLabel && !this.button.disabled) this.button.textContent = this.designLabel;
      this.onClick = () => {
        if (this.atc.disabled) return;
        if (this.design) this.showDesign();
        else this.atc.click();
      };
      this.onVariant = (event) => this.syncVariant(event.detail);
      this.onResize = () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.watchBuy(), 200);
      };
      this.button.addEventListener('click', this.onClick);
      document.addEventListener('yahav:variant-change', this.onVariant);
      window.addEventListener('resize', this.onResize);

      this.watchBuy();
      const footer = document.querySelector('footer');
      if (footer) {
        // A fast scroll can batch several entries for one target; the last one is current.
        this.footerObserver = new IntersectionObserver((entries) => {
          this.atFooter = entries[entries.length - 1].isIntersecting;
          this.render();
        });
        this.footerObserver.observe(footer);
      }
      document.body.classList.add('yp-sticky-ready');
    }

    disconnectedCallback() {
      this.buyObserver?.disconnect();
      this.footerObserver?.disconnect();
      clearTimeout(this.resizeTimer);
      this.button?.removeEventListener('click', this.onClick);
      document.removeEventListener('yahav:variant-change', this.onVariant);
      window.removeEventListener('resize', this.onResize);
      document.body.classList.remove('yp-sticky-on', 'yp-sticky-ready');
    }

    watchBuy() {
      this.buyObserver?.disconnect();
      const header = Math.round(document.querySelector('[data-header-height]')?.getBoundingClientRect().height || 0);
      this.buyObserver = new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        // Past means above the header line (the root's top edge), not merely off-screen.
        this.pastBuy = !entry.isIntersecting && entry.boundingClientRect.top < (entry.rootBounds?.top || 0);
        this.render();
      }, { rootMargin: `-${header}px 0px 0px 0px` });
      this.buyObserver.observe(this.buyRow);
    }

    render() {
      const show = this.pastBuy && !this.atFooter;
      this.classList.toggle('is-visible', show);
      this.inert = !show;
      this.setAttribute('aria-hidden', String(!show));
      document.body.classList.toggle('yp-sticky-on', show);
      if (show) document.body.style.setProperty('--yp-sticky-height', `${this.offsetHeight}px`);
    }

    showDesign() {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.design.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      this.design.querySelector('[data-yp-design-idea]')?.focus({ preventScroll: true });
      this.design.classList.remove('is-highlighted');
      void this.design.offsetWidth;
      this.design.classList.add('is-highlighted');
    }

    syncVariant(detail) {
      if (!detail || (this.productId && detail.productId !== this.productId)) return;
      const variant = detail.variant;
      this.button.disabled = !variant?.available;
      const addLabel = this.designLabel || this.labels.add;
      this.button.textContent = !variant ? this.labels.unavailable : (variant.available ? addLabel : this.labels.soldOut);
      if (!variant) return;
      const format = this.root.dataset.moneyFormat;
      this.price.textContent = money(variant.price, format);
      this.compare.hidden = !(variant.compare_at_price > variant.price);
      if (!this.compare.hidden) this.compare.textContent = money(variant.compare_at_price, format);
      if (this.root.querySelector('[data-yp-option]')) {
        this.variantLabel.textContent = variant.title;
        this.variantLabel.hidden = false;
      }
      const src = variant.featured_media?.preview_image?.src;
      if (this.image && src) {
        this.image.removeAttribute('srcset');
        this.image.src = `${src}${src.includes('?') ? '&' : '?'}width=160`;
      }
    }
  }

  customElements.define('yahav-sticky-atc', YahavStickyAtc);
})();
